import { useMemo } from "react";
import { useSharedState } from "state";

interface SortersProps {}

export default function Sorters({}: SortersProps) {
  const { sortersList, setSortersList, handleSort } = useSharedState();

  const isTaskPage = useMemo((): boolean => {
    return window.location.pathname === "/tasks";
  }, [window.location.pathname]);

  const isDocumentPage = useMemo((): boolean => {
    return window.location.pathname.includes("/documents");
  }, [window.location.pathname]);

  const isPaymentPage = useMemo((): boolean => {
    return window.location.pathname.includes("/payments");
  }, [window.location.pathname]);

  const isBudgetPage = useMemo((): boolean => {
    return window.location.pathname.includes("/budgets");
  }, [window.location.pathname]);

  const handleSortChange = (field: string, direction: string) => {
    const newSorters = [...sortersList];
    const sortString = `${field}:${direction}`;
    const existingIndex = newSorters.findIndex((s) => s.startsWith(`${field}:`));

    if (existingIndex >= 0) {
      if (direction === "") {
        newSorters.splice(existingIndex, 1);
      } else {
        newSorters[existingIndex] = sortString;
      }
    } else if (direction !== "") {
      newSorters.push(sortString);
    }

    setSortersList(newSorters);
  };

  const getSortValue = (field: string) => {
    const sorter = sortersList.find((s) => s.startsWith(`${field}:`));
    return sorter ? sorter.split(":")[1] : "";
  };

  const handleApply = () => {
    handleSort(sortersList);
  };

  const handleReset = () => {
    setSortersList([]);
    handleSort([]);
  };

  return (
    <>
      <a
        className="btn btn-ghost-primary grow position-relative"
        data-bs-toggle="offcanvas"
        href="#sorters"
        role="button"
        aria-controls="sorters"
      >
        Сортировка
        {sortersList.length > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
            {sortersList.length}
          </span>
        )}
      </a>

      <div className="offcanvas offcanvas-end" tabIndex={-1} id="sorters" aria-labelledby="sortersLabel">
        <div className="offcanvas-header">
          <h2 className="offcanvas-title" id="filtersAndSortersLabel">
            Сортировка
          </h2>
          <button
            type="button"
            className="btn-close text-reset"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          ></button>
        </div>
        <div className="offcanvas-body">
          <div className="d-flex flex-column gap-3">
            {(isTaskPage || isPaymentPage) && (
              <div>
                <label className="form-label">Наименование</label>
                <div className="form-selectgroup w-full">
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name={isTaskPage ? "task_name" : "title"}
                      value="asc"
                      checked={getSortValue(isTaskPage ? "task_name" : "title") === "asc"}
                      onChange={(e) => handleSortChange(isTaskPage ? "task_name" : "title", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По возрастанию</span>
                  </label>
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name={isTaskPage ? "task_name" : "title"}
                      value="desc"
                      checked={getSortValue(isTaskPage ? "task_name" : "title") === "desc"}
                      onChange={(e) => handleSortChange(isTaskPage ? "task_name" : "title", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По убыванию</span>
                  </label>
                </div>
              </div>
            )}

            {isTaskPage && (
              <div>
                <label className="form-label">Срок</label>
                <div className="form-selectgroup w-full">
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="deadline"
                      value="asc"
                      checked={getSortValue("deadline") === "asc"}
                      onChange={(e) => handleSortChange("deadline", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По возрастанию</span>
                  </label>
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="deadline"
                      value="desc"
                      checked={getSortValue("deadline") === "desc"}
                      onChange={(e) => handleSortChange("deadline", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По убыванию</span>
                  </label>
                </div>
              </div>
            )}

            {isTaskPage && (
              <div>
                <label className="form-label">Приоритетность</label>
                <div className="form-selectgroup w-full">
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="priority"
                      value="asc"
                      checked={getSortValue("priority") === "asc"}
                      onChange={(e) => handleSortChange("priority", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По возрастанию</span>
                  </label>
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="priority"
                      value="desc"
                      checked={getSortValue("priority") === "desc"}
                      onChange={(e) => handleSortChange("priority", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По убыванию</span>
                  </label>
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div>
                <label className="form-label">Контрагент</label>
                <div className="form-selectgroup w-full">
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="contragent"
                      value="asc"
                      checked={getSortValue("contragent") === "asc"}
                      onChange={(e) => handleSortChange("contragent", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По возрастанию</span>
                  </label>
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="contragent"
                      value="desc"
                      checked={getSortValue("contragent") === "desc"}
                      onChange={(e) => handleSortChange("contragent", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По убыванию</span>
                  </label>
                </div>
              </div>
            )}

            {(isDocumentPage || isPaymentPage) && (
              <div>
                <label className="form-label">Номер договора</label>
                <div className="form-selectgroup w-full">
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name={isDocumentPage ? "lease_number" : "dogovor"}
                      value="asc"
                      checked={getSortValue(isDocumentPage ? "lease_number" : "dogovor") === "asc"}
                      onChange={(e) => handleSortChange(isDocumentPage ? "lease_number" : "dogovor", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По возрастанию</span>
                  </label>
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name={isDocumentPage ? "lease_number" : "dogovor"}
                      value="desc"
                      checked={getSortValue(isDocumentPage ? "lease_number" : "dogovor") === "desc"}
                      onChange={(e) => handleSortChange(isDocumentPage ? "lease_number" : "dogovor", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По убыванию</span>
                  </label>
                </div>
              </div>
            )}

            {(isTaskPage || isDocumentPage || isPaymentPage) && (
              <div>
                <label className="form-label">Дата заявки</label>
                <div className="form-selectgroup w-full">
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="request_date"
                      value="asc"
                      checked={getSortValue("request_date") === "asc"}
                      onChange={(e) => handleSortChange("request_date", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По возрастанию</span>
                  </label>
                  <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                    <input
                      type="radio"
                      name="request_date"
                      value="desc"
                      checked={getSortValue("request_date") === "desc"}
                      onChange={(e) => handleSortChange("request_date", e.target.value)}
                      className="form-selectgroup-input"
                    />
                    <span className="form-selectgroup-label">По убыванию</span>
                  </label>
                </div>
              </div>
            )}

            {isBudgetPage && (
              <>
                <div>
                  <label className="form-label">Дата заявки</label>
                  <div className="form-selectgroup w-full">
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="request_date"
                        value="asc"
                        checked={getSortValue("request_date") === "asc"}
                        onChange={(e) => handleSortChange("request_date", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По возрастанию</span>
                    </label>
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="request_date"
                        value="desc"
                        checked={getSortValue("request_date") === "desc"}
                        onChange={(e) => handleSortChange("request_date", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По убыванию</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="form-label">Компания</label>
                  <div className="form-selectgroup w-full">
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="company"
                        value="asc"
                        checked={getSortValue("company") === "asc"}
                        onChange={(e) => handleSortChange("company", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По возрастанию</span>
                    </label>
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="company"
                        value="desc"
                        checked={getSortValue("company") === "desc"}
                        onChange={(e) => handleSortChange("company", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По убыванию</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="form-label">Отдел</label>
                  <div className="form-selectgroup w-full">
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="department"
                        value="asc"
                        checked={getSortValue("department") === "asc"}
                        onChange={(e) => handleSortChange("department", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По возрастанию</span>
                    </label>
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="department"
                        value="desc"
                        checked={getSortValue("department") === "desc"}
                        onChange={(e) => handleSortChange("department", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По убыванию</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="form-label">Бюджет</label>
                  <div className="form-selectgroup w-full">
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="budget_type"
                        value="asc"
                        checked={getSortValue("budget_type") === "asc"}
                        onChange={(e) => handleSortChange("budget_type", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По возрастанию</span>
                    </label>
                    <label className="form-selectgroup-item" style={{ width: "calc(50% - 4px)" }}>
                      <input
                        type="radio"
                        name="budget_type"
                        value="desc"
                        checked={getSortValue("budget_type") === "desc"}
                        onChange={(e) => handleSortChange("budget_type", e.target.value)}
                        className="form-selectgroup-input"
                      />
                      <span className="form-selectgroup-label">По убыванию</span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
            <button className="btn btn-ghost-primary" type="button" onClick={handleReset} data-bs-dismiss="offcanvas">
              Сбросить сортировку
            </button>
            <button className="btn btn-primary" type="button" onClick={handleApply} data-bs-dismiss="offcanvas">
              Применить
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
